import { Badge, Tooltip } from '@mantine/core';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import type {
  DriveDecorationIcon,
  DriveFolderDecoration,
} from '../../registry/defineDriveDecorations';

/**
 * Как рисуется каждый значок словаря декораций — ЕДИНАЯ точка на все
 * поверхности Диска. Инстанс выбирает смысл (`icon: 'check'`), платформа
 * решает, какой иконкой его показать; новое значение добавляется здесь и в
 * `DriveDecorationIcon`, и сразу работает в дереве, списке и плитке.
 */
const ICONS: Record<DriveDecorationIcon, typeof IconCircleCheckFilled> = {
  check: IconCircleCheckFilled,
};

/**
 * DriveDecorationMark — пометка папки у её имени: значок и/или бейдж.
 *
 * Вынесена из дерева и списка в общий компонент, потому что поверхностей уже
 * две и они обязаны выглядеть одинаково: иначе «текущая неделя» в дереве и в
 * списке разъедутся при первой же правке одной из них.
 *
 * Значок предпочтительнее бейджа в плотной строке: он не сдвигает имя папки,
 * рядом с которым и так живут значок зоны, аватарки участников и ховер-меню.
 * Смысл значка объясняет тултип — сама по себе галочка ничего не сообщает.
 */
export function DriveDecorationMark({
  decoration,
  size = 16,
}: {
  decoration: DriveFolderDecoration | null;
  /** Размер значка, px (дерево и список — 16, плитка крупнее). */
  size?: number;
}) {
  if (!decoration) return null;

  const Icon = decoration.icon ? ICONS[decoration.icon] : null;
  const color = `var(--mantine-color-${decoration.accent ?? 'blue'}-6)`;

  return (
    <>
      {Icon && (
        <Tooltip label={decoration.tooltip ?? ''} withArrow disabled={!decoration.tooltip}>
          {/* flexShrink: 0 — значок не должен сжиматься в узкой колонке */}
          <Icon size={size} color={color} style={{ flexShrink: 0 }} />
        </Tooltip>
      )}
      {decoration.badge && (
        <Tooltip label={decoration.tooltip ?? ''} withArrow disabled={!decoration.tooltip}>
          <Badge size='xs' variant='light' color={decoration.accent ?? 'blue'}>
            {decoration.badge}
          </Badge>
        </Tooltip>
      )}
    </>
  );
}
